/**
 * Datos de la organizacion para los datos estructurados (Schema.org).
 *
 * Se definen aqui como configuracion fija en lugar de derivarlos de
 * `translations` porque alli el NAP viene mezclado con maquetacion: los
 * telefonos llevan el prefijo "Tfno.:" y los correos vienen dentro de un
 * `<a href="mailto:...">`. Parsear ese HTML con expresiones regulares seria
 * fragil para un dato que cambia muy rara vez.
 *
 * Origen de los datos: `contacto.delegaciones` de src/translations/index.js y
 * los enlaces de redes sociales de src/components/parts/footer.vue. Si cambian
 * alli, hay que actualizarlos aqui.
 *
 * Deliberadamente NO se declaran `legalName` ni `vatID`: no constan en el
 * repositorio y en datos estructurados un dato inventado es peor que uno
 * ausente. Se pueden anadir cuando el cliente los confirme.
 */
import { CANONICAL_ORIGIN } from './head';

export const ORGANIZATION_NAME = 'AJUSA';

/**
 * Ano de fundacion. Lo confirma el propio texto de la web en ingles: "Since our
 * inception in 1972...". Enriquece la entidad de cara al grafo de conocimiento.
 */
export const FOUNDING_YEAR = '1972';

/** Identificador estable de la entidad dentro del grafo JSON-LD. */
export const ORGANIZATION_ID = CANONICAL_ORIGIN + '/#organization';

/** Identificador estable del sitio web dentro del grafo JSON-LD. */
export const WEBSITE_ID = CANONICAL_ORIGIN + '/#website';

/**
 * Logo de la organizacion. Google no admite SVG en `Organization.logo`, asi que
 * se usa el PNG que genera build/generate-og-images.js a partir de
 * static/img/logo.svg.
 */
export const ORGANIZATION_LOGO = {
  url: CANONICAL_ORIGIN + '/static/og/logo-ajusa.png',
  width: 1200,
  height: 486,
};

/** Perfiles oficiales, tal y como se enlazan desde el pie de pagina. */
export const SAME_AS = [
  'https://www.facebook.com/AjusaSpain/',
  'https://www.instagram.com/ajusa_automotive/',
  'https://www.linkedin.com/company/ajusa',
  'https://twitter.com/ajusa_spain',
  'https://www.youtube.com/channel/UCnoYVeFbsPiOVOcf80tW6Bg',
];

/** Sede central (Albacete). Las coordenadas salen de `contacto.coords`. */
export const HEADQUARTERS = {
  streetAddress: 'Parque Empresarial Ajusa, Calle 1, nº 1',
  postalCode: '02008',
  addressLocality: 'Albacete',
  addressRegion: 'Albacete',
  addressCountry: 'ES',
  postOfficeBoxNumber: '415',
  telephone: '+34 967 216 212',
  faxNumber: '+34 967 216 214',
  email: 'ajusa@ajusa.es',
  latitude: 39.0034405,
  longitude: -1.8193904,
};

/**
 * Delegaciones. Los telefonos se reproducen tal y como figuran en
 * `contacto.delegaciones`, salvo el del Reino Unido: alli aparece como
 * "+44 (0)195 2608 771" y el "(0)" no es valido en notacion internacional
 * (los digitos son los mismos).
 */
export const OFFICES = [
  {
    name: 'AJUSA',
    areaServed: 'ES',
    telephone: '+34 967 216 212',
    email: 'ajusa@ajusa.es',
    availableLanguage: ['es', 'en'],
  },
  {
    name: 'Ajusa UK Ltd',
    areaServed: 'GB',
    telephone: '+44 195 2608 771',
    email: 'info@ajusa.co.uk',
    availableLanguage: ['en'],
  },
  {
    name: 'Ajusa México S.A. de C.V',
    areaServed: 'MX',
    telephone: '+52 55 5254 2332',
    email: 'ajusamexico@ajusa.com.mx',
    availableLanguage: ['es'],
  },
  {
    name: 'Ajusa do Brasil Ltda.',
    areaServed: 'BR',
    telephone: '+55 11 3622-5353',
    email: 'ajusadobrasil@ajusa.com.br',
    availableLanguage: ['pt'],
  },
  {
    name: 'AJUSA USA Corp.',
    areaServed: 'US',
    telephone: '+1 310 625 1034',
    email: 'ajusausa@ajusa.es',
    availableLanguage: ['en', 'es'],
  },
  {
    name: 'Ajusa France',
    areaServed: 'FR',
    telephone: '+33 587 14 71 17',
    email: 'ajusafrance@ajusa.fr',
    availableLanguage: ['fr'],
  },
];

/** Direcciones postales completas de las delegaciones fuera de Espana. */
export const OFFICE_ADDRESSES = {
  GB: {
    streetAddress: 'Unit 25, Hortonwood 33',
    addressLocality: 'Telford',
    postalCode: 'TF1 7EX',
    addressCountry: 'GB',
  },
  MX: {
    streetAddress: 'Calle Lago Ladoga, 295-A, Col. Anáhuac, Deleg. Miguel Hidalgo',
    addressLocality: 'Ciudad de México',
    postalCode: '11320',
    addressCountry: 'MX',
  },
  BR: {
    streetAddress: 'Rua Santa Francisca, 417, Bairro Vila Jaguara',
    addressLocality: 'São Paulo',
    addressRegion: 'SP',
    postalCode: '05116-090',
    addressCountry: 'BR',
  },
  US: {
    streetAddress: 'Gables International Plaza, 2655 S. Le Jeune Road, Suite 905',
    addressLocality: 'Coral Gables',
    addressRegion: 'FL',
    postalCode: '33134',
    addressCountry: 'US',
  },
  FR: {
    streetAddress: '64, Rue Paul Claudel',
    addressLocality: 'Limoges',
    postalCode: '87000',
    addressCountry: 'FR',
  },
};

/** `PostalAddress` de la sede, reutilizado por Organization y ContactPage. */
export function buildHeadquartersAddress() {
  return {
    '@type': 'PostalAddress',
    streetAddress: HEADQUARTERS.streetAddress,
    postalCode: HEADQUARTERS.postalCode,
    addressLocality: HEADQUARTERS.addressLocality,
    addressRegion: HEADQUARTERS.addressRegion,
    addressCountry: HEADQUARTERS.addressCountry,
    postOfficeBoxNumber: HEADQUARTERS.postOfficeBoxNumber,
  };
}

/** Puntos de contacto de las 6 delegaciones. */
export function buildContactPoints() {
  return OFFICES.map(function (office) {
    const contactPoint = {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      name: office.name,
      telephone: office.telephone,
      email: office.email,
      areaServed: office.areaServed,
      availableLanguage: office.availableLanguage,
    };

    if (OFFICE_ADDRESSES[office.areaServed]) {
      contactPoint.address = Object.assign(
        { '@type': 'PostalAddress' },
        OFFICE_ADDRESSES[office.areaServed]
      );
    }

    return contactPoint;
  });
}

/**
 * Entidad `Organization` completa. Se emite una sola vez por pagina y el resto
 * de nodos del grafo la referencian por `@id`.
 */
export function buildOrganization() {
  return {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: ORGANIZATION_NAME,
    url: CANONICAL_ORIGIN + '/',
    foundingDate: FOUNDING_YEAR,
    logo: {
      '@type': 'ImageObject',
      url: ORGANIZATION_LOGO.url,
      width: ORGANIZATION_LOGO.width,
      height: ORGANIZATION_LOGO.height,
    },
    image: ORGANIZATION_LOGO.url,
    telephone: HEADQUARTERS.telephone,
    faxNumber: HEADQUARTERS.faxNumber,
    email: HEADQUARTERS.email,
    address: buildHeadquartersAddress(),
    geo: {
      '@type': 'GeoCoordinates',
      latitude: HEADQUARTERS.latitude,
      longitude: HEADQUARTERS.longitude,
    },
    contactPoint: buildContactPoints(),
    sameAs: SAME_AS,
  };
}



// WEBPACK FOOTER //
// ./src/seo/organization.js